import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react'
import AppLayout from '../layouts/AppLayout'
import { AppContext } from '../contexts/AppContext'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Spinner from 'react-activity/dist/Spinner'
import 'react-activity/dist/Spinner.css'
import { format } from 'date-fns'
import { enUS } from 'date-fns/locale'
import { IoIosArrowForward } from 'react-icons/io'
import WorkCover from '../components/WorkCover'
import { sortPeriods } from '../constants/domain'

// Member view: Works in a category, grouped by reporting period
const MemberMatches = () => {
  const { competition: competitionParam } = useParams()
  const navigate = useNavigate()
  const { getdaymatches, getmatchdays, isLoading, getCompetitions } = useContext(AppContext)

  const [matches, setMatches] = useState([])
  const [matchday, setMatchday] = useState()
  const [matchdays, setMatchdays] = useState([])
  const [matchesLoading, setmatchesLoading] = useState(false)
  const [competitions, setCompetitions] = useState([])

  const fetchCompetitions = useCallback(async () => {
    const response = await getCompetitions()
    setCompetitions(response || [])
  }, [getCompetitions])

  useEffect(() => {
    fetchCompetitions()
  }, [fetchCompetitions])

  // Without a category in the URL, open the default (or first) one
  const competition = useMemo(() => {
    if (competitionParam) return competitionParam
    const fallback = competitions.find((c) => c.isDefault) || competitions[0]
    return fallback ? String(fallback.id) : undefined
  }, [competitionParam, competitions])

  const selectedCompetition = useMemo(
    () => competitions.find((comp) => comp.id === Number(competition)),
    [competitions, competition]
  )

  const fetchMatchDays = async () => {
    const response = await getmatchdays(competition)
    if (!response) return
    const periods = sortPeriods(response.matchdays || [], (p) => p.matchday).reverse()
    setMatchdays(periods)
    if (response.currentmatchdays.length > 0) {
      setMatchday(response.currentmatchdays[0].matchday)
      setMatches(response.currentmatchdays)
    } else if (periods.length > 0) {
      setMatchday(periods[0].matchday)
    }
  }

  const fetchMatchDaysMatches = async () => {
    if (!matchday) return
    setmatchesLoading(true)
    const response = await getdaymatches(matchday, competition)
    setmatchesLoading(false)
    setMatches(response || [])
  }

  useEffect(() => {
    if (competition) fetchMatchDays()
  }, [competition])

  useEffect(() => {
    fetchMatchDaysMatches()
  }, [matchday])

  return (
    <AppLayout pageName='Works'>
      <div className='flex flex-wrap gap-2 mb-4'>
        {competitions.map((cmp) => (
          <button
            key={cmp.id}
            onClick={() => navigate(`/matches/${cmp.id}`)}
            className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
              Number(competition) === cmp.id ? 'bg-main text-white border-main' : 'bg-white hover:bg-neutral-50'
            }`}
          >
            {cmp.name}
          </button>
        ))}
      </div>
      {isLoading && matchdays.length === 0 ? (
        <div className='flex justify-center w-full py-12'>
          <Spinner
            color='#CCCCCC'
            size={24}
          />
        </div>
      ) : (
        <div className='space-y-3'>
          {matchdays.length === 0 && (
            <section className='text-neutral-500'>No works are being monitored in {selectedCompetition?.name || 'this category'} yet.</section>
          )}
          {matchdays.map((day, index) => {
            return (
              <section
                className='!p-0'
                key={index}
              >
                <div
                  className={`py-5 px-5 rounded-lg bg-white hover:bg-neutral-50 transition-colors cursor-pointer flex flex-row justify-between space-x-2 items-center ${
                    matchday === day.matchday && 'border-b'
                  }`}
                  onClick={() => setMatchday(matchday === day.matchday ? '' : day.matchday)}
                >
                  <h5 className='text-lg leading-none font-semibold tracking-wide'>{day.matchday}</h5>
                  <span className={`${matchday === day.matchday && 'rotate-90'}`}>
                    <IoIosArrowForward />
                  </span>
                </div>
                {matchday === day.matchday && (
                  <>
                    {matchesLoading ? (
                      <div className='flex justify-center w-full py-12'>
                        <Spinner
                          color='#CCCCCC'
                          size={24}
                        />
                      </div>
                    ) : (
                      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 px-5 py-7'>
                        {matches.map((match) => (
                          <Link
                            key={match.matchid}
                            to={`/matches/${competition}/${match.matchid}`}
                          >
                            <div className='flex items-center gap-4 py-4 px-5 rounded-lg bg-neutral-50 hover:bg-neutral-100 border transition-colors'>
                              <WorkCover
                                title={match.title}
                                cover={match.cover}
                                size={56}
                              />
                              <div className='flex-1 min-w-0'>
                                <div className='text-lg font-semibold text-black truncate'>{match.title}</div>
                                <div className='text-sm text-neutral-500'>
                                  {[match.author, match.language].filter(Boolean).join(' · ')}
                                </div>
                              </div>
                              <div className='text-right text-xs text-neutral-500'>
                                <div className='font-medium text-neutral-700'>{match.status}</div>
                                <div>since {format(new Date(match.date), 'do MMM', { locale: enUS })}</div>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </section>
            )
          })}
        </div>
      )}
    </AppLayout>
  )
}

export default MemberMatches
